import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ChevronDown, Globe, Search } from 'lucide-react';
import {
  APP_STORE_LOCALIZATIONS,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';

export type LocaleSelectMenuProps = {
  value: string;
  onChange: (locale: string) => void;
  /** Override catalog; defaults to APP_STORE_LOCALIZATIONS. */
  locales?: { value: string; label: string }[];
  /** Locales shown first under “In use” (e.g. filled / primary+targets). */
  preferredLocales?: string[];
  /** Shows primary short badge next to this locale. */
  primaryLocale?: string;
  /** Optional trailing content per option (warning icon, count, …). */
  renderOptionExtra?: (locale: string) => ReactNode;
  id?: string;
  /** Opens menu aligned to the right edge of the trigger. */
  menuAlign?: 'left' | 'right';
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
};

/**
 * Searchable App Store locale picker — shared by App Screens / Version toolbars.
 * Trigger: Globe + flag + code (+ optional primary badge) + chevron.
 */
const LocaleSelectMenu: React.FC<LocaleSelectMenuProps> = ({
  value,
  onChange,
  locales = APP_STORE_LOCALIZATIONS,
  preferredLocales = [],
  primaryLocale,
  renderOptionExtra,
  id = 'locale-select',
  menuAlign = 'left',
  disabled = false,
  className,
  buttonClassName,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const knownValues = useMemo(() => new Set(locales.map(l => l.value)), [locales]);

  const currentValue = knownValues.has(value)
    ? value
    : preferredLocales.find(l => knownValues.has(l)) ??
      locales[0]?.value ??
      value;

  const currentLabel = locales.find(l => l.value === currentValue)?.label ?? currentValue;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (loc: { value: string; label: string }) =>
      !q ||
      loc.value.toLowerCase().includes(q) ||
      loc.label.toLowerCase().includes(q);

    const priority = new Set(preferredLocales.filter(Boolean));
    // Keep preferred order from preferredLocales, then catalog order for the rest.
    const preferred = preferredLocales
      .map(code => locales.find(l => l.value === code))
      .filter((l): l is { value: string; label: string } => l != null)
      .filter(matches);
    const rest = locales.filter(l => !priority.has(l.value) && matches(l));
    return { preferred, rest };
  }, [locales, preferredLocales, search]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectLocale = (locale: string) => {
    onChange(locale);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={clsx('flex items-center gap-2', className)} ref={rootRef}>
      <div className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen(o => !o)}
          aria-label={t('language.label')}
          aria-expanded={open}
          aria-haspopup="listbox"
          title={`${currentLabel} — ${t('appScreen.localeSelectTitle')}`}
          className={clsx(
            'inline-flex items-center gap-1.5 h-8 pl-2 pr-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:pointer-events-none',
            buttonClassName
          )}
        >
          <Globe className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
          <span className="text-base leading-none" aria-hidden>
            {getLocaleFlag(currentValue)}
          </span>
          <span className="tabular-nums tracking-tight">{currentValue}</span>
          {primaryLocale && currentValue === primaryLocale && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
              {t('appScreen.primaryShort')}
            </span>
          )}
          <ChevronDown
            className={clsx(
              'h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
        {open && (
          <div
            role="listbox"
            aria-labelledby={id}
            className={clsx(
              'absolute top-full z-40 mt-1 w-64 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg overflow-hidden',
              menuAlign === 'right' ? 'right-0' : 'left-0'
            )}
          >
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <input
                  type="search"
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t('appScreen.localeSearchPlaceholder')}
                  className="w-full pl-7 pr-2 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filtered.preferred.length === 0 && filtered.rest.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                  {t('appScreen.localeSearchEmpty')}
                </p>
              ) : (
                <>
                  {filtered.preferred.length > 0 && (
                    <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {t('appScreen.localeGroupActive')}
                    </div>
                  )}
                  {filtered.preferred.map(loc => (
                    <LocaleOptionRow
                      key={loc.value}
                      loc={loc}
                      selected={loc.value === currentValue}
                      isPrimary={Boolean(primaryLocale && loc.value === primaryLocale)}
                      primaryLabel={t('appScreen.primaryShort')}
                      extra={renderOptionExtra?.(loc.value)}
                      onSelect={selectLocale}
                    />
                  ))}
                  {filtered.rest.length > 0 && (
                    <div className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {t('appScreen.localeGroupAll')}
                    </div>
                  )}
                  {filtered.rest.map(loc => (
                    <LocaleOptionRow
                      key={loc.value}
                      loc={loc}
                      selected={loc.value === currentValue}
                      isPrimary={Boolean(primaryLocale && loc.value === primaryLocale)}
                      primaryLabel={t('appScreen.primaryShort')}
                      extra={renderOptionExtra?.(loc.value)}
                      onSelect={selectLocale}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function LocaleOptionRow({
  loc,
  selected,
  isPrimary,
  primaryLabel,
  extra,
  onSelect,
}: {
  loc: { value: string; label: string };
  selected: boolean;
  isPrimary: boolean;
  primaryLabel: string;
  extra?: ReactNode;
  onSelect: (locale: string) => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={() => onSelect(loc.value)}
      className={clsx(
        'w-full grid grid-cols-[1.25rem_4.75rem_minmax(0,1fr)] items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors',
        selected
          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80'
      )}
    >
      <span className="text-base leading-none text-center" aria-hidden>
        {getLocaleFlag(loc.value)}
      </span>
      <span className="font-medium tabular-nums tracking-tight">{loc.value}</span>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="truncate text-gray-500 dark:text-gray-400 text-xs">{loc.label}</span>
        {isPrimary && (
          <span className="shrink-0 text-[10px] font-semibold uppercase text-primary-600 dark:text-primary-400">
            {primaryLabel}
          </span>
        )}
        {extra}
      </span>
    </button>
  );
}

export default LocaleSelectMenu;
